import { useEffect, useMemo, useState } from "react";

import { isNumberString } from "@corca/core";

import { collectPriorDomains } from "@/components/suggestions/providers/utils/getPhantomHints";
import { wrapperScope } from "@/components/suggestions/providers/utils/scopeExpressions";
import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";
import { GraphExpressionNodeModel } from "@/models/nodes/GraphExpressionNodeModel";
import { GraphNodeModel } from "@/models/nodes/GraphNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { GeogebraVisitor } from "@/models/nodes/visitors/GeogebraVisitor";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { isEqualsSign, isRelation } from "@/models/utils/semanticChecks";

import { LABEL_COLORS } from "./labelColors";

import type { GraphOptions } from "@/components/graph/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeType } from "@corca/data";
import type { NodeJson } from "@corca/data";

export function getSelectedExpressions(doc: DocumentModel, limit?: number): NodeModel[][] {
  if (doc.cursor.isActive) {
    const { selectedNodes } = doc.cursorContext().selection;
    if (selectedNodes.length === 1 && selectedNodes[0].is(VariableNodeModel)) {
      const varNode = selectedNodes[0];
      const nextNode = varNode.nextSiblings[0];
      if (isRelation(nextNode)) {
        const nodes: NodeModel[] = [varNode, nextNode];
        for (const node of nextNode.nextSiblings) {
          if (isRelation(node)) break;
          nodes.push(node);
        }
        return [nodes];
      }
    }
    return selectedNodes.length > 0 ? [selectedNodes] : [];
  }
  const { tiptap } = doc.editor;
  if (!tiptap) return [];

  const { from, to } = doc.editor.textSelection;
  const expressions: NodeModel[][] = [];
  const tiptapContentSize = tiptap.state.doc?.content.size || 0;

  tiptap.state.doc.nodesBetween(
    Math.min(tiptapContentSize, from),
    Math.min(tiptapContentSize, to),
    (node) => {
      if (typeof limit === "number" && expressions.length >= limit) {
        return;
      }

      if (nodeIsExpression(node)) {
        const { nodeId } = node.attrs as { nodeId: string };
        const wrapper = doc.nodes.get(nodeId);
        if (wrapper?.is(WrapperNodeModel)) {
          expressions.push(wrapper.inlineNode?.nodes ?? []);
        }
      }
    },
  );
  return expressions;
}

export type XAxisBounds = { min: number; max: number };

// Returns null for non-numeric intervals (set symbols like ℤ, or ∞ endpoints).
function intervalNodeToBounds(node: NodeJson): XAxisBounds | null {
  if (node.type !== "group") return null;
  const matrix = node.children.find((n) => n.type === "matrix");
  const inline = matrix?.children.find((n) => n.type === "inline");
  if (!inline) return null;

  const isComma = (n: NodeJson) =>
    n.type === "operatorSymbol" &&
    n.children[0]?.type === "symbol" &&
    n.children[0].props.value === ",";

  const commaIndex = inline.children.findIndex(isComma);
  if (commaIndex < 0) return null;

  // A negative endpoint is [minus operatorSymbol, number] since number nodes reject leading "-".
  const numberValue = (nodes: NodeJson[]): number | null => {
    let sign = 1;
    let rest = nodes;
    const first = nodes[0];
    if (first?.type === "operatorSymbol" && first.children[0]?.type === "symbol") {
      const { key } = first.children[0].props;
      if (key === "symbol-subtraction") sign = -1;
      if (key === "symbol-subtraction" || key === "symbol-plus") rest = nodes.slice(1);
    }
    if (rest.length !== 1 || rest[0].type !== "number") return null;
    const value = Number(rest[0].props.value);
    return Number.isFinite(value) ? sign * value : null;
  };

  const min = numberValue(inline.children.slice(0, commaIndex));
  const max = numberValue(inline.children.slice(commaIndex + 1));
  if (min === null || max === null) return null;

  return { min, max };
}

export const EXPRESSION_COUNT_LIMIT = 4;

/**
 * A lone variable/constant/number ("x", "5", "pi") is too trivial to graph, and a lone
 * function ("sin(x)") already gets a graph in the sidebar's function panel. A single
 * operator/group node (e.g. a fraction "x^2/(1+x^2)") is graphable.
 */
const NON_GRAPHABLE_SINGLE_NODES: NodeType[] = ["variable", "constant", "number", "function"];

type GraphableOptions = {
  allowSingleNode?: NodeType[];
};

export function canGraphExpressions(
  expressions: NodeModel[][],
  { allowSingleNode = [] }: GraphableOptions = {},
): boolean {
  if (expressions.length === 0 || expressions.length >= EXPRESSION_COUNT_LIMIT) {
    return false;
  }

  const [first] = expressions;

  if (expressions.length !== 1 || first.length !== 1) {
    return true;
  }

  const { type } = first[0];

  return !NON_GRAPHABLE_SINGLE_NODES.includes(type) || allowSingleNode.includes(type);
}

export function getXAxisBounds(doc: DocumentModel, xAxisVariable: string): XAxisBounds | undefined {
  const domains = collectPriorDomains(wrapperScope(doc.textNode.wrapperNodes)).get(xAxisVariable);
  if (!domains) return undefined;

  let bounds: XAxisBounds | undefined;
  for (const domain of domains) {
    const next = intervalNodeToBounds(domain.domainNode);
    if (!next) continue;
    bounds = bounds
      ? { min: Math.max(bounds.min, next.min), max: Math.min(bounds.max, next.max) }
      : next;
  }
  return bounds;
}

// Wrap a GeoGebra expression in an `If` over the x range. For `y = ...` only the RHS is
// wrapped to keep the equation valid. Endpoint openness is not represented (negligible on a curve).
export function restrictExpressionToXRange(expression: string, bounds: XAxisBounds): string {
  const condition = `${bounds.min} <= x <= ${bounds.max}`;
  const eqIndex = expression.indexOf("=");
  if (eqIndex !== -1) {
    const lhs = expression.slice(0, eqIndex + 1);
    const afterEq = expression.slice(eqIndex + 1);
    const leadingSpace = afterEq.slice(0, afterEq.length - afterEq.trimStart().length);
    const rhs = afterEq.trim();
    return `${lhs}${leadingSpace}If(${condition}, ${rhs})`;
  }
  return `If(${condition}, ${expression.trim()})`;
}

export function buildExpression(
  nodeExpressions: NodeModel[][],
  axisVariables: { x: string; y: string },
  xAxisBounds?: XAxisBounds,
): {
  expression: string;
  rangedValueExpression: string;
  rangedValues: Record<string, number>;
} {
  const expressions: string[] = [];
  const rangedValues: Record<string, number> = {};

  const definedVariables = new Set<string>([axisVariables.x, axisVariables.y]);

  for (const nodeExpression of nodeExpressions) {
    const node = nodeExpression[0];
    if (node?.is(VariableNodeModel) || node?.is(ConstantNodeModel)) {
      const nextNode = nodeExpression[1];
      if (isEqualsSign(nextNode) && node.value) {
        definedVariables.add(node.value);
      }
    }
  }

  for (const nodeExpression of nodeExpressions) {
    const visitor = new GeogebraVisitor({
      xAxisVariable: axisVariables.x,
      yAxisVariable: axisVariables.y,
    });

    for (const node of nodeExpression) {
      const valueNodes = [node, ...node.descendants].filter((n) => {
        if (n.is(VariableNodeModel)) {
          return !n.hasParams;
        }
        if (n.is(ConstantNodeModel)) {
          switch (n.symbolNode?.props.key) {
            case "symbol-pi":
              return false;
            case "constant-eulers-number":
              return false;
            default:
              return true;
          }
        }
        return false;
      }) as NodeModel<"variable" | "constant">[];

      for (const valueNode of valueNodes) {
        const name = valueNode.symbolNode?.labeledValue;
        if (name && !rangedValues[name] && !definedVariables.has(name)) {
          rangedValues[name] =
            valueNode.value && isNumberString(valueNode.value) ? Number(valueNode.value) : 1;
        }
      }
    }

    // FIXME: risky assertion (use plain object visitor here)
    const visited = visitor.visitInlineNode({ nodes: nodeExpression } as NodeModel<"inline">);
    expressions.push(xAxisBounds ? restrictExpressionToXRange(visited, xAxisBounds) : visited);
  }

  return {
    expression: expressions.join("\n").trim(),
    rangedValues,
    rangedValueExpression: Object.entries(rangedValues)
      .map(([name, value]) => `${name} = ${value}`)
      .join("\n"),
  };
}

export function getGraphCursorContextId(node: GraphExpressionNodeModel) {
  return `graph-expression-${node.id}`;
}

export function moveToGraphExpression(node?: GraphExpressionNodeModel) {
  if (!node?.inlineNode) return;

  const { doc } = node;

  doc.cursorContextId = getGraphCursorContextId(node);
  doc.cursor.moveTo(node.inlineNode, node.inlineNode.nodes.length);
}

export function moveToNextGraphExpression(node: GraphExpressionNodeModel) {
  if (!node.parent?.is(GraphNodeModel)) return;

  const { graphExpressionNodes } = node.parent;
  const index = graphExpressionNodes.indexOf(node);

  moveToGraphExpression(graphExpressionNodes[index + 1]);
}

export function moveToPrevGraphExpression(node: GraphExpressionNodeModel) {
  if (!node.parent?.is(GraphNodeModel)) return;

  const { graphExpressionNodes } = node.parent;
  const index = graphExpressionNodes.indexOf(node);

  moveToGraphExpression(graphExpressionNodes[index - 1]);
}

export function addGraphExpression(node: GraphNodeModel, afterNode?: GraphExpressionNodeModel) {
  const { doc, graphExpressionNodes } = node;

  let graphExpressionNode: GraphExpressionNodeModel | undefined;

  doc.transact(() => {
    const color = LABEL_COLORS[graphExpressionNodes.length % LABEL_COLORS.length];

    graphExpressionNode = doc.create(GraphExpressionNodeModel, { color }, [
      doc.create(InlineNodeModel, []),
    ]);

    if (afterNode) {
      afterNode.insertSiblingAfter(graphExpressionNode);
    } else {
      node.appendChild(graphExpressionNode);
    }
  }, "Graph:addExpression");

  if (graphExpressionNode) {
    queueMicrotask(() => {
      moveToGraphExpression(graphExpressionNode);
    });
  }
}

export function removeGraphExpression(node: GraphExpressionNodeModel) {
  const { doc, parent } = node;
  const graphNode = parent?.is(GraphNodeModel) ? parent : undefined;
  const index = graphNode?.graphExpressionNodes.indexOf(node);

  doc.transact(() => {
    doc.removeNode(node);
  }, "Graph:removeExpression");

  if (typeof index === "number") {
    const prevGraphExpressionNode = graphNode?.graphExpressionNodes[Math.max(0, index - 1)];

    if (prevGraphExpressionNode) {
      queueMicrotask(() => {
        moveToGraphExpression(prevGraphExpressionNode);
      });
    }
  }
}

export function calculateGraphCoordsSystem(
  options: Pick<Partial<GraphOptions>, "zoom" | "width" | "height" | "coordsSystem">,
): [number, number, number, number] | undefined {
  if (!options.width || !options.height) {
    return undefined;
  }

  const scale = (options.zoom ?? 100) / 100;
  const pixelsPerUnit = 55 * scale;
  const xRange = options.width / (2 * pixelsPerUnit);
  const yRange = options.height / (2 * pixelsPerUnit);

  const [xmin, xmax, ymin, ymax] = options.coordsSystem ?? [];

  return [xmin ?? -xRange, xmax ?? xRange, ymin ?? -yRange, ymax ?? yRange];
}

export function useAxisVariables(selectedExpressions: NodeModel[][]) {
  const defaultAxisVariables = useMemo(() => {
    const vars = new Set<string>();
    for (const expr of selectedExpressions) {
      for (const exprNode of expr) {
        for (const node of [exprNode, ...exprNode.descendants]) {
          if (node.is(VariableNodeModel)) {
            vars.add(node.value);
          }
        }
      }
    }

    const noAxisVars = vars.difference(new Set(["x", "y"]));

    let xVar = vars.has("x") ? "x" : undefined;
    if (!xVar && noAxisVars.size === 1) {
      xVar = Array.from(noAxisVars)[0];
      noAxisVars.delete(xVar);
    }
    let yVar = vars.has("y") ? "y" : undefined;
    if (!yVar && noAxisVars.size === 1) {
      yVar = Array.from(noAxisVars)[0];
    }

    // check for equations like "f = ..."
    if (
      !yVar &&
      selectedExpressions[0]?.[0]?.is(VariableNodeModel) &&
      isEqualsSign(selectedExpressions[0][1])
    ) {
      const firstVar = selectedExpressions[0][0];
      yVar = firstVar.value;
      noAxisVars.delete(yVar);
      if (noAxisVars.size === 1 && !xVar) {
        xVar = Array.from(noAxisVars)[0];
      }
    }

    return {
      x: xVar || "x",
      y: yVar || "y",
    };
  }, [selectedExpressions]);

  const [axisVariables, setAxisVariables] = useState<{ x: string; y: string }>(
    defaultAxisVariables,
  );

  useEffect(() => {
    setAxisVariables({
      x: defaultAxisVariables.x,
      y: defaultAxisVariables.y,
    });
  }, [defaultAxisVariables.x, defaultAxisVariables.y]);

  return { axisVariables, setAxisVariables };
}
